// src/components/Auth/AuthSuccessScreen.tsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';

interface AuthSuccessScreenProps {
  providerName?: string;
  userName?: string;
  email?: string;
  onContinue: () => void;
  autoRedirectDelay?: number; // Time in ms before auto-redirecting
}

export function AuthSuccessScreen({
  providerName,
  userName,
  email,
  onContinue,
  autoRedirectDelay = 3000 // Default 3 seconds
}: AuthSuccessScreenProps) {
  // Auto-redirect after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      onContinue();
    }, autoRedirectDelay);

    return () => clearTimeout(timer);
  }, [onContinue, autoRedirectDelay]);

  // Get display name for the provider
  const getProviderDisplay = () => {
    if (!providerName) return '';

    switch (providerName.toLowerCase()) {
      case 'google':
        return 'Google';
      case 'microsoft':
        return 'Microsoft';
      case 'apple':
        return 'Apple';
      default:
        return providerName;
    }
  };


  const handleContinue = () => {
    // Clear the socialAuthSuccess flag to ensure we don't show this screen again on next visit
    sessionStorage.removeItem('socialAuthSuccess');
    sessionStorage.removeItem('socialAuthProvider');
    sessionStorage.removeItem('socialAuthEmail');
    
    // Call the provided onContinue callback
    onContinue();
  };

  // Get welcome message based on available info
  const getWelcomeMessage = () => {
    if (userName) {
      return `Welcome, ${userName}!`;
    }
    return 'Welcome to Tripper!';
  };

  // Get provider message
  const getProviderMessage = () => {
    if (!providerName) {
      return 'You have successfully signed in.';
    }
    return `You have successfully signed in with ${getProviderDisplay()}.`;
  };

  return (
    <>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Authentication Successful</h2>
        <p className="text-sm text-gray-500 mt-1">
          Your account has been verified
        </p>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col items-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: 'spring' }}
          className="mb-6"
        >
          <CheckCircle size={64} className="text-green-500" style={{ 
            width: 'var(--auth-success-icon-size)', 
            height: 'var(--auth-success-icon-size)' 
          }} />
        </motion.div>

        <h2 className="text-xl font-semibold text-center mb-2">
          {getWelcomeMessage()}
        </h2>
        
        <p className="text-gray-600 text-center mb-1">
          {getProviderMessage()}
        </p>

        {email && (
          <p className="text-gray-500 text-sm text-center mb-6">
            Signed in as: <span className="font-medium">{email}</span>
          </p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="w-full"
        >
          <button
            onClick={handleContinue}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Continue to Tripper
          </button>
        </motion.div>

        <div className="mt-8 text-center">
          <div className="inline-block bg-gray-200 rounded-full overflow-hidden" style={{
            height: 'var(--auth-progress-bar-height)',
            width: '64px'
          }}>
            <motion.div
              className="h-full bg-blue-600"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: autoRedirectDelay / 1000, ease: 'linear' }}
            />
          </div>
          <p className="text-gray-500" style={{
            fontSize: 'var(--auth-font-size-xs)',
            marginTop: 'var(--auth-compact-spacing)'
          }}>
            Continuing automatically in {Math.ceil(autoRedirectDelay / 1000)} seconds...
          </p>
        </div>
      </div>
    </>
  );
}